import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import {
  ShieldCheck, ArrowRight, CheckCircle2, Calendar, Bell, User, Lock,
  AlertCircle, AlertTriangle, Mail, Eye, Send, FileSearch,
  LayoutDashboard, Users, ClipboardCheck, BellRing, FileText, BarChart3, Settings,
  TrendingDown, Clock, Award,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import Footer from "../components/Footer";
import BookDemoDialog from "../components/BookDemoDialog";

const HERO_BULLETS = [
  "Prevent expired credentials",
  "Automate reminders & escalations",
  "Complete audit trail",
  "Stay survey-ready every day",
];

const TRUST_INDICATORS = [
  { icon: Lock,        title: "No PHI",             sub: "We don't store patient data" },
  { icon: ShieldCheck, title: "HIPAA-Safe",         sub: "Workforce compliance only" },
  { icon: CheckCircle2,title: "Secure & Encrypted", sub: "Enterprise-grade security" },
];

const VALUE_BLOCKS = [
  { icon: TrendingDown, title: "Reduce Compliance Risk", text: "Avoid fines and survey findings." },
  { icon: Clock,        title: "Save Time",              text: "Automate repetitive tasks." },
  { icon: Eye,          title: "Increase Accountability",text: "Every action is logged." },
  { icon: Award,        title: "Stay Survey-Ready",      text: "Every single day." },
];

// Generic neutral wordmarks (placeholder until pilot customers come on board)
const TRUST_LOGOS = [
  { name: "BrightCare", sub: "HOME HEALTH" },
  { name: "FirstChoice", sub: "HOME CARE" },
  { name: "Compassionate", sub: "CARE SERVICES" },
  { name: "Always Best", sub: "CARE" },
  { name: "Family First", sub: "HOME CARE" },
  { name: "Ultimate", sub: "HOME CARE" },
];

const SIDEBAR_ITEMS = [
  { label: "Dashboard",     icon: LayoutDashboard, active: true },
  { label: "Caregivers",    icon: Users },
  { label: "Credentials",   icon: ClipboardCheck },
  { label: "Alerts",        icon: BellRing },
  { label: "Notifications", icon: Bell },
  { label: "Audit Logs",    icon: FileText },
  { label: "Reports",       icon: BarChart3 },
  { label: "Settings",      icon: Settings },
];

export default function Landing() {
  const { user } = useAuth();
  const [demoOpen, setDemoOpen] = useState(false);
  if (user && user !== false) return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen bg-white">
      {/* NAV */}
      <nav className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-[#E8E6E1]">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="landing-logo">
            <div className="w-10 h-10 border-2 border-[#2E5C50] rounded-md flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-[#2E5C50]" strokeWidth={2} />
            </div>
            <div className="leading-tight">
              <p className="font-heading font-bold text-xl tracking-wide text-[#1C211F]">NEXARI</p>
              <p className="text-[10px] tracking-[0.3em] font-medium text-[#57605A] -mt-0.5">HEALTH</p>
            </div>
          </Link>
          <div className="hidden md:flex items-center gap-7 text-sm font-medium text-[#1C211F]">
            <a href="#how" className="hover:text-[#2E5C50] transition-colors">How It Works</a>
            <Link to="/pricing" data-testid="landing-nav-pricing" className="hover:text-[#2E5C50] transition-colors">Pricing</Link>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/auth" data-testid="nav-signin-btn" className="text-sm font-medium text-[#1C211F] hover:text-[#2E5C50] px-3 py-2">Login</Link>
            <button data-testid="nav-book-demo-btn" onClick={() => setDemoOpen(true)}
              className="bg-[#2E5C50] text-white hover:bg-[#22463D] transition-colors rounded-md px-5 py-2.5 text-sm font-semibold shadow-sm">
              Book a Demo
            </button>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <section className="max-w-7xl mx-auto px-6 pt-14 pb-20 grid lg:grid-cols-[1fr_1.15fr] gap-12 items-center">
        <div className="fade-in-up">
          {/* Pill eyebrow */}
          <span className="inline-flex items-center gap-1.5 bg-[#E6F0EB] text-[#2E5C50] border border-[#34745C]/15 px-3.5 py-1.5 rounded-full text-[11px] font-semibold tracking-wide">
            <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={2.5} />
            WORKFORCE COMPLIANCE PLATFORM FOR HOME CARE AGENCIES
          </span>

          {/* Headline */}
          <h1 className="mt-7 font-heading text-4xl sm:text-5xl lg:text-[3.4rem] leading-[1.08] tracking-tight text-[#1C211F]">
            Know You&rsquo;re Compliant
            <br />
            <span className="text-[#2E5C50]">Before the Surveyor Does.</span>
          </h1>

          {/* Subhead */}
          <p className="mt-6 text-base md:text-lg leading-relaxed text-[#57605A] max-w-xl">
            Nexari tracks certifications, automates reminders, escalates issues, and documents every
            action—so you never miss another credential expiration again.
          </p>

          {/* Bulleted benefits */}
          <ul className="mt-7 grid grid-cols-2 gap-x-6 gap-y-3 max-w-lg">
            {HERO_BULLETS.map((b) => (
              <li key={b} className="flex items-start gap-2 text-sm text-[#1C211F]">
                <span className="mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#2E5C50] shrink-0">
                  <CheckCircle2 className="w-3 h-3 text-white" strokeWidth={3} />
                </span>
                {b}
              </li>
            ))}
          </ul>

          {/* CTAs */}
          <div className="mt-9 flex flex-wrap items-center gap-3">
            <button data-testid="hero-book-demo-btn" onClick={() => setDemoOpen(true)}
              className="group inline-flex items-center gap-2 bg-[#2E5C50] text-white hover:bg-[#22463D] transition-colors rounded-md px-7 py-3.5 font-semibold text-base shadow-sm">
              <Calendar className="w-4 h-4" strokeWidth={2} />
              Book a Demo
            </button>
            <Link to="/auth" data-testid="hero-see-dashboard-btn"
              className="group inline-flex items-center gap-2 border border-[#1C211F]/15 bg-white text-[#1C211F] hover:border-[#2E5C50]/50 transition-colors rounded-md px-7 py-3.5 font-semibold text-base">
              See the Dashboard
              <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" strokeWidth={2} />
            </Link>
          </div>

          {/* Trust row */}
          <div className="mt-10 grid grid-cols-3 gap-5 max-w-lg pt-8 border-t border-[#E8E6E1]">
            {TRUST_INDICATORS.map((t) => (
              <div key={t.title} data-testid={`hero-trust-${t.title.toLowerCase().replace(/[^a-z]+/g,'-')}`}>
                <div className="flex items-center gap-2">
                  <t.icon className="w-4 h-4 text-[#2E5C50]" strokeWidth={2} />
                  <p className="text-sm font-semibold text-[#1C211F]">{t.title}</p>
                </div>
                <p className="mt-0.5 text-xs text-[#57605A]">{t.sub}</p>
              </div>
            ))}
          </div>
        </div>

        {/* DASHBOARD MOCKUP — full app w/ sidebar */}
        <div data-testid="hero-dashboard-mockup" className="fade-in-up" style={{ animationDelay: "120ms" }}>
          <div className="bg-white border border-[#E8E6E1] rounded-2xl shadow-2xl overflow-hidden grid grid-cols-[180px_1fr]">
            {/* Left sidebar */}
            <aside className="bg-[#22463D] text-white py-4 px-3">
              <div className="flex items-center gap-2 px-2 pb-4 mb-2 border-b border-white/10">
                <div className="w-7 h-7 border border-white/40 rounded flex items-center justify-center">
                  <ShieldCheck className="w-4 h-4 text-white" strokeWidth={2} />
                </div>
                <span className="font-heading font-bold text-sm tracking-wider">NEXARI</span>
              </div>
              <nav className="space-y-0.5">
                {SIDEBAR_ITEMS.map((s) => (
                  <div key={s.label}
                    className={`flex items-center gap-2.5 px-2.5 py-2 rounded-md text-[11px] font-medium ${
                      s.active ? "bg-white/15 text-white" : "text-white/65 hover:bg-white/5"
                    }`}>
                    <s.icon className="w-3.5 h-3.5" strokeWidth={1.8} />
                    {s.label}
                  </div>
                ))}
              </nav>
            </aside>

            {/* Main panel */}
            <div className="p-4 space-y-4">
              {/* Top bar */}
              <div className="flex items-center justify-between">
                <div className="font-heading font-semibold text-sm text-[#1C211F]">Agency Compliance Overview</div>
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Bell className="w-4 h-4 text-[#1C211F]" strokeWidth={1.6} />
                    <span className="absolute -top-1 -right-1.5 bg-[#B54D40] text-white text-[8px] font-bold rounded-full w-3.5 h-3.5 flex items-center justify-center">3</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-6 h-6 bg-[#E8E6E1] rounded-full flex items-center justify-center">
                      <User className="w-3 h-3 text-[#57605A]" strokeWidth={1.8} />
                    </div>
                    <div className="leading-tight">
                      <p className="text-[10px] font-semibold text-[#1C211F]">Maria Santos</p>
                      <p className="text-[8px] text-[#57605A]">Admin</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* 5 KPI cards */}
              <div className="grid grid-cols-5 gap-2">
                {/* Half-circle compliance gauge */}
                <div className="bg-white border border-[#E8E6E1] rounded-md p-2.5 flex flex-col items-center">
                  <div className="relative w-14 h-9 overflow-hidden">
                    <svg viewBox="0 0 36 18" className="w-full h-full">
                      <path d="M 4 18 A 14 14 0 0 1 32 18" fill="none" stroke="#D6E0D9" strokeWidth="3" strokeLinecap="round" />
                      <path d="M 4 18 A 14 14 0 0 1 32 18" fill="none" stroke="#34745C" strokeWidth="3" strokeLinecap="round"
                            strokeDasharray="40, 100" pathLength="100" />
                    </svg>
                  </div>
                  <p className="font-heading text-base font-bold text-[#34745C] leading-tight mt-0.5">92%</p>
                  <p className="text-[7px] uppercase tracking-wider font-semibold text-[#34745C] leading-tight">Compliant</p>
                  <p className="text-[7px] text-[#57605A] mt-0.5">Target: 95%</p>
                </div>
                <KpiCol value="2"  color="#B54D40" label="Expired Credentials" />
                <KpiCol value="7"  color="#C48231" label="Expiring This Month" />
                <KpiCol value="14" color="#3B6FB8" label="Reminders Sent" />
                <KpiCol value="3"  color="#34745C" label="Renewals Completed" />
              </div>

              {/* Bottom two panels */}
              <div className="grid grid-cols-2 gap-2">
                {/* Recent Alerts */}
                <div className="bg-white border border-[#E8E6E1] rounded-md p-2.5">
                  <div className="flex items-center justify-between mb-1.5">
                    <p className="text-[10px] font-semibold text-[#1C211F]">Recent Alerts</p>
                    <span className="text-[8px] font-semibold text-[#2E5C50]">View all</span>
                  </div>
                  <div className="space-y-1">
                    {[
                      { name: "James O'Connor",  cred: "Background Check",  note: "Expired 2 days ago", color: "#B54D40", bg: "#FCEAE8", tag: "Expired",       tagBg: "#FCEAE8", tagColor: "#B54D40" },
                      { name: "Maria Santos",    cred: "CPR Certification", note: "Expires in 7 days",  color: "#C48231", bg: "#FDF3E1", tag: "Expiring Soon", tagBg: "#FDF3E1", tagColor: "#C48231" },
                      { name: "Aisha Patel",     cred: "TB Test",           note: "Expires in 22 days", color: "#C48231", bg: "#FDF3E1", tag: "Expiring Soon", tagBg: "#FDF3E1", tagColor: "#C48231" },
                    ].map((r) => (
                      <div key={r.name} className="flex items-center gap-1.5 px-1.5 py-1 rounded" style={{ background: r.bg + "55" }}>
                        <Bell className="w-2.5 h-2.5 shrink-0" style={{ color: r.color }} strokeWidth={2.2} />
                        <div className="flex-1 min-w-0 leading-tight">
                          <p className="text-[9px] font-semibold text-[#1C211F] truncate">{r.name} – {r.cred}</p>
                          <p className="text-[7px] text-[#57605A] truncate">{r.note}</p>
                        </div>
                        <span className="text-[7px] font-semibold px-1.5 py-0.5 rounded-full" style={{ background: r.tagBg, color: r.tagColor }}>{r.tag}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Notification Activity */}
                <div className="bg-white border border-[#E8E6E1] rounded-md p-2.5">
                  <p className="text-[10px] font-semibold text-[#1C211F] mb-2">Notification Activity (30 Days)</p>
                  <div className="space-y-1.5">
                    <NotifRow icon={Mail}         color="#3B6FB8" value="184"  label="Notifications Sent" />
                    <NotifRow icon={CheckCircle2} color="#34745C" value="97%"  label="Delivered" />
                    <NotifRow icon={Send}         color="#C48231" value="73%"  label="Acknowledged" />
                  </div>
                  <p className="mt-2 text-[8px] font-semibold text-[#2E5C50]">View all activity →</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 4 VALUE BLOCKS */}
      <section id="how" className="bg-[#F9F7F4] border-y border-[#E8E6E1]">
        <div className="max-w-7xl mx-auto px-6 py-10 grid grid-cols-2 lg:grid-cols-4 gap-5">
          {VALUE_BLOCKS.map((b) => (
            <div key={b.title} className="flex items-start gap-3">
              <div className="w-10 h-10 border border-[#2E5C50]/25 rounded-md flex items-center justify-center shrink-0">
                <b.icon className="w-5 h-5 text-[#2E5C50]" strokeWidth={1.6} />
              </div>
              <div>
                <p className="font-heading font-semibold text-sm text-[#1C211F]">{b.title}</p>
                <p className="mt-0.5 text-xs text-[#57605A]">{b.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* TRUST LOGOS STRIP */}
      <section className="bg-white">
        <div className="max-w-7xl mx-auto px-6 py-14">
          <p className="text-center text-[11px] uppercase tracking-[0.28em] font-semibold text-[#57605A]">
            Trusted by Home Care Agencies Across the Country
          </p>
          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-6 items-center">
            {TRUST_LOGOS.map((l) => (
              <div key={l.name} className="text-center text-[#57605A]/70 hover:text-[#57605A] transition-colors">
                <p className="font-heading font-bold text-sm tracking-tight">{l.name}</p>
                <p className="text-[9px] tracking-[0.24em] font-semibold mt-0.5">{l.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AUDIT PACKET HIGHLIGHT */}
      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="bg-[#1C211F] text-white rounded-2xl p-10 md:p-14 grid lg:grid-cols-2 gap-10 items-center">
          <div>
            <p className="text-[11px] uppercase tracking-[0.28em] font-semibold text-[#C07A60]">Survey day</p>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl tracking-tight">
              Hand the surveyor a <span className="font-bold text-[#D6E0D9]">tamper-evident packet.</span> Pass.
            </h2>
            <p className="mt-4 text-base text-white/70 max-w-md leading-relaxed">
              Generate a complete audit trail — every credential, every reminder, every escalation —
              for any date range. One click. SHA-256 fingerprinted.
            </p>
            <button data-testid="landing-audit-cta" onClick={() => setDemoOpen(true)}
              className="mt-6 inline-flex items-center gap-2 bg-white text-[#1C211F] hover:bg-[#F9F7F4] transition-colors rounded-md px-6 py-3 text-sm font-semibold">
              See it in action <ArrowRight className="w-4 h-4" strokeWidth={2} />
            </button>
          </div>
          <div className="space-y-3">
            {[
              { icon: ShieldCheck, label: "Tamper-evident PDF (SHA-256)" },
              { icon: Calendar,    label: "Any date range" },
              { icon: FileSearch,  label: "Audit logs + credentials + employees — combined" },
              { icon: Mail,        label: "Proof of notification" },
            ].map((f) => (
              <div key={f.label} className="flex items-center gap-3 bg-white/[0.06] border border-white/10 rounded-md p-3">
                <f.icon className="w-5 h-5 text-[#D6E0D9] shrink-0" strokeWidth={1.5} />
                <span className="text-sm text-white/90">{f.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="bg-[#2E5C50] text-white">
        <div className="max-w-5xl mx-auto px-6 py-16 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight">
            Stop chasing expirations. <span className="font-bold">Start passing surveys.</span>
          </h2>
          <p className="mt-3 text-base text-white/80 max-w-xl mx-auto">
            Built for U.S. home-care agencies. No patient data. No medical records. Just workforce compliance.
          </p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            <button data-testid="landing-final-book-demo" onClick={() => setDemoOpen(true)}
              className="inline-flex items-center gap-2 bg-white text-[#1C211F] hover:bg-[#F9F7F4] transition-colors rounded-md px-7 py-3 font-semibold">
              <Calendar className="w-4 h-4" strokeWidth={2} /> Book a Demo
            </button>
            <Link to="/pricing" data-testid="landing-final-pricing"
              className="inline-flex items-center gap-2 border border-white/30 text-white hover:bg-white/10 transition-colors rounded-md px-7 py-3 font-semibold">
              View pricing
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <BookDemoDialog open={demoOpen} onClose={() => setDemoOpen(false)} />
    </div>
  );
}

function KpiCol({ value, color, label }) {
  return (
    <div className="bg-white border border-[#E8E6E1] rounded-md p-2.5 flex flex-col items-center">
      <p className="font-heading text-xl font-bold leading-tight" style={{ color }}>{value}</p>
      <p className="text-[8px] uppercase tracking-wider font-semibold text-[#57605A] text-center mt-1 leading-tight">{label}</p>
      <p className="text-[8px] font-semibold text-[#2E5C50] mt-1">View all</p>
    </div>
  );
}

function NotifRow({ icon: Icon, color, value, label }) {
  return (
    <div className="flex items-center gap-2">
      <div className="w-6 h-6 rounded-md flex items-center justify-center" style={{ background: color + "1A" }}>
        <Icon className="w-3 h-3" style={{ color }} strokeWidth={1.8} />
      </div>
      <div className="flex-1 leading-tight">
        <p className="font-heading text-sm font-bold text-[#1C211F]">{value}</p>
        <p className="text-[8px] text-[#57605A]">{label}</p>
      </div>
    </div>
  );
}
